import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { SortKey } from '@/types/db';
import { NEAREST_CATEGORIES, type NearestCategory } from '@/lib/nearest-constants';
import { cn } from '@/lib/utils';

/** The pair the dashboard cares about: a sort key + (for nearest) its target. */
export interface SortValue {
  sort: SortKey;
  nearestCat: NearestCategory;
}

export interface SortSelectProps {
  value: SortValue;
  onChange: (next: SortValue) => void;
  className?: string;
}

/** Base (non-amenity) sort options, in display order. */
const BASE_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'recency', label: 'Recency' },
  { value: 'price', label: 'Price (low→high)' },
  { value: 'composite', label: 'Best Fit' },
  { value: 'dog', label: 'Dog Fit' },
  { value: 'walk_ent', label: 'Walk + Ent' },
];

// Radix Select needs one string value per item. Nearest options encode their
// category as `nearest:<cat>` so the single dropdown drives both `sort` and
// `nearestCat`.
const NEAREST_PREFIX = 'nearest:';

/** Map a {sort, nearestCat} pair to the single Radix item value. */
export function encodeSortValue(value: SortValue): string {
  return value.sort === 'nearest' ? `${NEAREST_PREFIX}${value.nearestCat}` : value.sort;
}

/**
 * Decode a Radix item value back into {sort, nearestCat}. Base sorts keep the
 * previously-selected nearestCat so flipping back to a nearest sort remembers it.
 */
export function decodeSortValue(raw: string, prevNearestCat: NearestCategory): SortValue {
  if (raw.startsWith(NEAREST_PREFIX)) {
    return { sort: 'nearest', nearestCat: raw.slice(NEAREST_PREFIX.length) as NearestCategory };
  }
  return { sort: raw as SortKey, nearestCat: prevNearestCat };
}

/**
 * `<SortSelect>` — grouped sort dropdown for the dashboard card stream.
 *
 * Two groups in one control:
 *   - "Sort by":        Recency · Price · Best Fit · Dog Fit · Walk + Ent
 *   - "Nearest amenity": Dog Park · Dog Beach · Daycare · Dog Bar ·
 *                        Coffee Shop · Restaurant · Convenience Store
 *
 * Picking a "Nearest amenity" entry sets `sort: 'nearest'` + the chosen
 * `nearestCat`; picking a base entry sets `sort` and preserves the last
 * `nearestCat` (so toggling back to a nearest sort remembers the target).
 */
export function SortSelect({ value, onChange, className }: SortSelectProps) {
  const selected = encodeSortValue(value);
  const handleChange = (v: string) => onChange(decodeSortValue(v, value.nearestCat));

  return (
    <Select value={selected} onValueChange={handleChange}>
      <SelectTrigger
        aria-label="Sort by"
        className={cn(
          'h-9 w-[190px] bg-transparent border-foam-50/15 text-foam-50',
          'hover:bg-foam-50/5 focus:ring-bronze-500/60',
          className,
        )}
      >
        <span className="text-sm font-semibold text-foam-50/80 mr-2">Sort:</span>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-ocean-950/95 backdrop-blur-md text-foam-50 border border-foam-50/10">
        <SelectGroup>
          <SelectLabel className="text-foam-50/50">Sort by</SelectLabel>
          {BASE_OPTIONS.map((opt) => (
            <SelectItem
              key={opt.value}
              value={opt.value}
              className="text-foam-50 focus:bg-ocean-800/80 focus:text-foam-50"
            >
              {opt.label}
            </SelectItem>
          ))}
        </SelectGroup>
        <SelectGroup>
          <SelectLabel className="text-foam-50/50">Nearest amenity</SelectLabel>
          {NEAREST_CATEGORIES.map((cat) => (
            <SelectItem
              key={cat.value}
              value={`${NEAREST_PREFIX}${cat.value}`}
              className="text-foam-50 focus:bg-ocean-800/80 focus:text-foam-50"
            >
              {cat.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
